/**
 * The pinned shared contract for Motion Kit.
 *
 * Everything in this file is imported by the editor, the rig tools, persistence, GLB I/O and
 * the runtime SDK. Treat the shapes below as frozen: add, never rename or repurpose.
 */

/**
 * `humanoid-v2` (2026-08-20): a canonical clip rotation is now measured from the **canonical
 * straight T-pose** (`REST_REFERENCE_POSITIONS`) instead of from each rig's own rest, via
 * `RigBinding.restAlignment`. Clips authored under `humanoid-v1` were rest-relative and are
 * reinterpreted by the new convention, so they are rejected rather than silently shifted.
 */
export const RIG_VERSION = 'humanoid-v2';

/** Rig versions this build can read. Anything else is a hard mismatch. */
export const SUPPORTED_RIG_VERSIONS: readonly string[] = [RIG_VERSION];
export const LIBRARY_SCHEMA_VERSION = 1;

export type CanonicalBoneId =
  | 'Root'
  | 'Hips'
  | 'Spine'
  | 'Chest'
  | 'Neck'
  | 'Head'
  | 'LeftUpperArm'
  | 'LeftLowerArm'
  | 'LeftHand'
  | 'RightUpperArm'
  | 'RightLowerArm'
  | 'RightHand'
  | 'LeftUpperLeg'
  | 'LeftLowerLeg'
  | 'LeftFoot'
  | 'RightUpperLeg'
  | 'RightLowerLeg'
  | 'RightFoot';

/** x, y, z, w — three.js order. Always normalized before serialization. */
export type QuaternionTuple = [number, number, number, number];
export type Vec3Tuple = [number, number, number];

export type TweenPreset =
  | 'linear'
  | 'easeIn'
  | 'easeOut'
  | 'easeInOut'
  | 'step'
  | 'sineIn'
  | 'sineOut'
  | 'sineInOut'
  | 'cubicIn'
  | 'cubicOut'
  | 'cubicInOut';

export interface RotationKeyframe {
  frame: number;
  rotation: QuaternionTuple;
  easing: TweenPreset;
  /**
   * Hips vertical offset — meaningful ONLY on the `Hips` track; never stored on any other bone.
   *
   * Dimensionless: `(Hips joint height at this frame − Hips bind height) / leg length`, along the
   * model's up axis, where leg length is the vertical bind-pose distance from the Hips joint to the
   * lower of the two Foot joints. Normalized so a crouch authored on one rig reads the same depth on
   * a taller or shorter one. `0` or absent = bind height; positive = up. Always finite when present.
   *
   * The channel is additive and optional: keys written before it existed carry no field, and the
   * evaluator interpolates across keys that lack it rather than treating them as zero — otherwise
   * every pre-existing key would yank a crouch back to bind height.
   */
  hipsY?: number;
}

/** Sorted by frame, unique frames. Rotation only — never position or scale (D8). */
export interface BoneRotationTrack {
  keyframes: RotationKeyframe[];
}

/** Payload a clip event carries. Flat and JSON-safe on purpose — see `ClipEvent`. */
export type ClipEventData = Record<string, string | number | boolean>;

/**
 * A named marker on a clip's timeline: a footstep, a hit frame, a combo window.
 *
 * Not bone-scoped — an event belongs to the clip, which is why it lives beside `tracks` rather
 * than inside one. It has **no id**: an event is identified by its `(frame, name)` pair and
 * `normalizeClip` drops exact duplicates. Adding an id later is an additive change; removing one
 * would not be, so the smaller format is the reversible choice.
 *
 * On a **looping** clip the legal range is half-open, `[0, durationFrames)`. `wrapFrame` treats a
 * clip as N frames, so frame N *is* frame 0 — an event authored at `durationFrames` would either
 * fire twice per loop or never. `normalizeClip` clamps it to `durationFrames - 1`.
 */
export interface ClipEvent {
  /** Integer frame >= 0. */
  frame: number;
  /** Trimmed, non-empty. NOT unique: the same name may appear at several frames. */
  name: string;
  /**
   * Optional payload, restricted to flat primitives so it survives JSON, strict import validation
   * and a deterministic re-emit without arbitrary depth. Keys are serialized SORTED — the clip
   * fingerprint that decides whether an exported file is stale compares raw bytes, and IndexedDB
   * hands records back in insertion order.
   */
  data?: ClipEventData;
}

export interface CanonicalAnimationClip {
  id: string;
  name: string;
  fps: number;
  durationFrames: number;
  loop: boolean;
  tracks: Partial<Record<CanonicalBoneId, BoneRotationTrack>>;
  /** Sorted by frame, then name. Omitted entirely when empty, so clip bytes stay unchanged. */
  events?: ClipEvent[];
  createdAt: number;
  updatedAt: number;
}

/**
 * The implicit default folder every clip belongs to until it is filed somewhere else.
 *
 * It is never stored: not in `LibraryFolderMeta.folders`, never as a `clipFolders` value. Storing
 * it would give "unfiled" two spellings (absent / "General") that could disagree after a rename.
 */
export const DEFAULT_FOLDER = 'General';

/**
 * Single-level clip folders. Deliberately metadata ON THE SIDE rather than a field on
 * `CanonicalAnimationClip`: three exhaustive re-projections of a clip record (storage
 * normalization, library JSON, import validation) would silently drop an extra field, and a
 * folder rename would become a write over every member record.
 */
export interface LibraryFolderMeta {
  /** User folders, excluding `DEFAULT_FOLDER`; trimmed, unique, insertion order. */
  folders: string[];
  /** clipId → folder name. Never `DEFAULT_FOLDER` (that is expressed by having no entry). */
  clipFolders: Record<string, string>;
}

export interface AnimationLibraryFile {
  schemaVersion: number;
  rigVersion: string;
  exportedAt: string;
  clips: CanonicalAnimationClip[];
  /** Additive (schema 1 stays 1): omitted when empty, ignored by older readers. */
  folders?: string[];
  /** Additive: clipId → folder name, omitted when empty. */
  clipFolders?: Record<string, string>;
}

/** offsets = W_b (see plan D5); identity binding = all identity. */
export interface RigBinding {
  rigVersion: string;
  canonicalToSourceBone: Partial<Record<CanonicalBoneId, string>>;
  restRotationOffsets: Partial<Record<CanonicalBoneId, QuaternionTuple>>;
  /**
   * Rotation bringing the source model's world frame to the canonical one (Y up, +Z forward).
   * Identity when the source model already matches the canonical convention.
   */
  worldCorrection: QuaternionTuple;
  /**
   * `E_b` — the minimal swing taking this bone's direction in the canonical straight T-pose
   * (`REST_REFERENCE_POSITIONS`) to its direction in **this rig's** rest pose. It is what makes a
   * clip authored on a T-pose rig play correctly on an A-pose one.
   *
   * A canonical clip stores the rig-independent `q_abs`; a rig consumes the rig-relative `q_rel`:
   *
   *     q_abs(b) = E(parent)^-1 * q_rel(b) * E(b)
   *     q_rel(b) = E(parent)   * q_abs(b) * E(b)^-1
   *
   * `parent` is the nearest *mapped* canonical ancestor, so an unmapped Chest never double-counts.
   * The correction is two-sided because `q_c` is applied in the bone's local slot and is therefore
   * parent-relative: a one-sided per-bone factor applies the parent's alignment twice on the child
   * (measured: elbow correct, hand 208 mm off).
   *
   * Absent or all-identity means "this rig's rest IS the canonical rest" — the pre-`humanoid-v2`
   * behaviour, and still exactly what a rig built on the canonical skeleton in a true T-pose gets.
   */
  restAlignment?: Partial<Record<CanonicalBoneId, QuaternionTuple>>;
}

export interface MappingPreset {
  id: string;
  name: string;
  rigVersion: string;
  canonicalToSourceBone: Partial<Record<CanonicalBoneId, string>>;
  createdAt: number;
}

/** RIG mode result (rest skeleton). */
export interface CanonicalRigPlacement {
  rigVersion: string;
  jointWorldPositions: Record<CanonicalBoneId, Vec3Tuple>;
}

export type Pose = Partial<Record<CanonicalBoneId, QuaternionTuple>>;

/**
 * Skin weights for one mesh. Typed arrays are directly storable in IndexedDB.
 * `skinIndex` and `skinWeight` are 4 entries per vertex (three.js attribute layout).
 */
export interface SkinData {
  meshName: string;
  vertexCount: number;
  skinIndex: Uint16Array;
  skinWeight: Float32Array;
}

/**
 * Per-joint skin correction. `offset` is metres in model space (positive grows the bone's
 * region); `blend` is a transition LENGTH in metres — the same unit and range as
 * `SkinParams.blend` (0.002..0.2, default 0.03) — and overrides it for this joint when set.
 */
export interface SkinJointParams {
  offset: number;
  blend?: number;
}

/** One-click region → bone reassignment; vertex indices of one mesh (all seam duplicates included). */
export interface SkinRegionOverride {
  meshName: string;
  vertices: number[];
  boneId: CanonicalBoneId;
}

/** Voxels along the longest bbox axis of the mesh. */
export type SkinResolution = 64 | 96 | 128 | 192;

/** Tunables of the voxel-geodesic auto-skinning pipeline. Persisted with the project. */
export interface SkinParams {
  version: 1;
  /** voxels along the longest bbox axis (default 128) */
  resolution: SkinResolution;
  /**
   * global blend width in METRES — the transition length τ of the solver's
   * `exp(-(d - dmin) / τ)` falloff (default 0.03, clamped to 0.002..0.2)
   */
  blend: number;
  /** Laplacian smoothing passes over the weights after the solve (default 2, capped at 50). */
  smoothingIterations: number;
  /** default 0.5 */
  smoothingLambda: number;
  /** default true — Left/Right joint params are edited together */
  symmetry: boolean;
  joints: Partial<Record<CanonicalBoneId, SkinJointParams>>;
  regionOverrides: SkinRegionOverride[];
}

export interface ProjectRecord {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
  /**
   * Wall-clock time the project was last opened in the editor. Set by `projectsRepo.markOpened`
   * on a successful open and by nothing else — in particular it is NOT `updatedAt`, which moves
   * on every autosave. Absent on records written by a build from before this field existed, so
   * every reader has to treat "never opened" as a normal state rather than a defect.
   */
  lastOpenedAt?: number;
  kind: 'rigged' | 'unrigged';
  sourceGlbBlobId: string;
  /** after skinning */
  riggedGlbBlobId?: string;
  binding: RigBinding | null;
  placement: CanonicalRigPlacement | null;
  selectedClipIds: string[];
  skinData?: SkinData[];
  skinParams?: SkinParams;
}

// ---------------------------------------------------------------------------
// Additions beyond the pinned contract (see report "Affects others")
// ---------------------------------------------------------------------------

/** One selected keyframe slot. Used by every clipOps mutation that acts on a selection. */
export interface KeyframeRef {
  bone: CanonicalBoneId;
  frame: number;
}

/** A selection of keyframe slots. Order is irrelevant; duplicates are tolerated. */
export type KeyframeSelection = KeyframeRef[];

/** A copied keyframe; `frameOffset` is relative to the minimum frame in the copied set. */
export interface ClipboardKeyframe {
  bone: CanonicalBoneId;
  frameOffset: number;
  rotation: QuaternionTuple;
  easing: TweenPreset;
  /**
   * Carried so copy → paste keeps a crouch's depth. Written back by `pasteKeyframes` ONLY when the
   * destination bone (after `boneRemap`) is `Hips` — see `RotationKeyframe.hipsY`.
   */
  hipsY?: number;
}

/**
 * Result of `copyKeyframes`; consumed by `pasteKeyframes`.
 *
 * `kind` is frozen at the pre-rename spelling so a clipboard payload copied before the rename still
 * pastes.
 */
export interface ClipboardPayload {
  kind: 'animstatex/keyframes';
  rigVersion: string;
  /** fps of the clip the keyframes were copied from (informational). */
  fps: number;
  keyframes: ClipboardKeyframe[];
}

/** Bake output shape produced by `sampleClipForBake` and consumed by GLB export (D9). */
export interface BakedClipSamples {
  times: Float32Array;
  rotations: Partial<Record<CanonicalBoneId, Float32Array>>;
  /**
   * One normalized Hips offset per sampled frame, same length as `times`. Present **iff** the clip
   * has at least one `hipsY` key: a rotation-only clip must bake to exactly what it baked before the
   * channel existed, so this is absent rather than an all-zero array.
   */
  hipsY?: Float32Array;
}

/** Result of `validateCanonicalHierarchy`. */
export interface HierarchyValidationResult {
  ok: boolean;
  errors: string[];
}

/** A two-bone IK chain over canonical bones. */
export type IkChainName = 'leftArm' | 'rightArm' | 'leftLeg' | 'rightLeg';

export interface IkChain {
  name: IkChainName;
  root: CanonicalBoneId;
  mid: CanonicalBoneId;
  end: CanonicalBoneId;
  side: 'left' | 'right';
  limb: 'arm' | 'leg';
  /** Bend direction used when the rest pose gives no usable one (elbows back, knees forward). */
  fallbackBendDir: Vec3Tuple;
}

/** Successful / failed parse of a library JSON document. */
export type LibraryParseResult =
  | { ok: true; file: AnimationLibraryFile; warnings: string[] }
  | { ok: false; errors: string[] };

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Canonical bones in hierarchy (parent-before-child) order. */
export const CANONICAL_BONE_IDS: readonly CanonicalBoneId[] = [
  'Root',
  'Hips',
  'Spine',
  'Chest',
  'Neck',
  'Head',
  'LeftUpperArm',
  'LeftLowerArm',
  'LeftHand',
  'RightUpperArm',
  'RightLowerArm',
  'RightHand',
  'LeftUpperLeg',
  'LeftLowerLeg',
  'LeftFoot',
  'RightUpperLeg',
  'RightLowerLeg',
  'RightFoot',
];

/**
 * Bones a mapping must supply. `Root` is optional: imported rigs frequently have no root bone,
 * while rigs built by this tool always do.
 */
export const REQUIRED_BONE_IDS: readonly CanonicalBoneId[] = CANONICAL_BONE_IDS.filter(
  (b) => b !== 'Root',
);

export const TWEEN_PRESETS: readonly TweenPreset[] = [
  'linear',
  'easeIn',
  'easeOut',
  'easeInOut',
  'step',
  'sineIn',
  'sineOut',
  'sineInOut',
  'cubicIn',
  'cubicOut',
  'cubicInOut',
];

export const DEFAULT_FPS = 30;
export const DEFAULT_EASING: TweenPreset = 'linear';
